/** The hero's behavior half: reads the studio and its services, builds the facts that have data. Draws nothing. */
import { useMemo } from 'react';
import { LOCATION_KIND_LABEL, type SiteModality, type SiteNextOpenings, type SiteServiceSummaryDto } from '@telify/shared';

import { mostBookedFirst, useNextOpenings, usePopularServiceIds, useSiteServices, useSiteStudio } from '../../api/site';
import { usePieceEmit, usePieceOptions, type PieceInput, type PieceRuntime } from '../../contract/runtime';
import { formatDayLabel, formatSlotTime, getClientTimezone } from '../../format';
import { usePageMover } from '../../pageMover';
import { contract, type HeroActions, type HeroEvents, type HeroFact, type HeroState } from './contract';

export type HeroRuntime = PieceRuntime<typeof contract, HeroState, HeroActions>;

/** The kinds in the order the Format fact names them: the first offered is its value, the others its note. */
const KINDS: readonly SiteModality[] = ['IN_PERSON', 'ONLINE', 'PHONE'];

const COUNT_WORDS = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve'];

/** YYYY-MM-DD of the day `offset` days from today, in `tz`. */
function dayIn(tz: string, offset: number): string {
  const today = new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(
    new Date(),
  );
  const d = new Date(`${today}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + offset);
  return d.toISOString().slice(0, 10);
}

function buildFacts(
  services: readonly SiteServiceSummaryDto[],
  openings: SiteNextOpenings | undefined,
  tz: string,
): HeroFact[] {
  if (services.length === 0) return [];
  const facts: HeroFact[] = [];

  // The next opening of the most booked service (the one Open times shows first, so the read is shared).
  const first = services[0]!;
  const day = openings?.days.find((d) => d.times.length > 0);
  const time = day?.times[0];
  if (day && time) {
    const word = day.date === dayIn(tz, 0) ? 'Today' : day.date === dayIn(tz, 1) ? 'Tomorrow' : formatDayLabel(time.startsAt, tz);
    facts.push({
      id: 'nextOpening',
      label: 'Next opening',
      value: `${word}, ${formatSlotTime(time.startsAt, tz)}`,
      note: first.name,
      href: `${first.path}?start=${encodeURIComponent(time.startsAt)}`,
    });
  }

  const lengths = services.map((s) => s.durationMinutes);
  const min = Math.min(...lengths);
  const max = Math.max(...lengths);
  const n = services.length;
  facts.push({
    id: 'length',
    label: 'Sessions',
    value: min === max ? `${min} min` : `${min} to ${max} min`,
    note: `${COUNT_WORDS[n] ?? n} ${n === 1 ? 'service' : 'services'}`,
    href: null,
  });

  const [firstKind = 'IN_PERSON', ...otherKinds] = KINDS.filter((k) => services.some((s) => s.modality === k));
  facts.push({
    id: 'format',
    label: 'Format',
    value: LOCATION_KIND_LABEL[firstKind],
    note: otherKinds.length > 0 ? `and ${otherKinds.map((k) => LOCATION_KIND_LABEL[k].toLowerCase()).join(' and ')}` : null,
    href: null,
  });
  return facts;
}

export function useHero(input: PieceInput<HeroEvents> = {}): HeroRuntime {
  const options = usePieceOptions(contract, input.options);
  const emit = usePieceEmit(input.onEvent);
  const navigate = usePageMover();
  const tz = useMemo(() => getClientTimezone(), []);
  const studioQuery = useSiteStudio();
  const servicesQuery = useSiteServices();
  const studio = studioQuery.data;
  const popularQuery = usePopularServiceIds();
  // Held until the ranking answers, as Open times holds its list; a failed ranking keeps the studio order.
  const services = useMemo(
    () =>
      servicesQuery.data && !popularQuery.isPending ? mostBookedFirst(servicesQuery.data, popularQuery.data ?? []) : undefined,
    [servicesQuery.data, popularQuery.isPending, popularQuery.data],
  );
  const firstId = services?.[0]?.id;
  const openingsQuery = useNextOpenings(firstId, tz);

  // Held until the next opening has an answer, so the row does not grow a fact after it draws.
  const facts = useMemo(
    () => (!services || (firstId && openingsQuery.isPending) ? [] : buildFacts(services, openingsQuery.data, tz)),
    [services, firstId, openingsQuery.isPending, openingsQuery.data, tz],
  );

  const eyebrow =
    [studio?.location?.trim(), studio?.establishedYear ? `Est. ${studio.establishedYear}` : null].filter(Boolean).join(' · ') ||
    null;
  const status: HeroState['status'] = studio ? 'ready' : studioQuery.isError ? 'error' : 'loading';

  const actions = useMemo<HeroActions>(
    () => ({
      openServices: () => {
        emit('navigated', { target: 'services' });
        navigate('/#services');
      },
      openEvents: () => {
        emit('navigated', { target: 'events' });
        navigate('/events');
      },
      openFact: (factId) => {
        const fact = facts.find((f) => f.id === factId);
        if (!fact?.href) return;
        emit('navigated', { target: factId });
        navigate(fact.href);
      },
    }),
    [facts, emit, navigate],
  );

  return {
    state: {
      status,
      name: studio?.name ?? '',
      tagline: studio?.tagline?.trim() || null,
      eyebrow,
      photoUrl: studio?.aboutPhotoUrl ?? null,
      hasServices: Boolean(studio?.hasServices),
      hasEvents: Boolean(studio?.hasEvents),
      facts,
    },
    actions,
    options,
  };
}
